import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaBullseye, FaColumns, FaMagic, FaCommentDots, FaMobileAlt } from 'react-icons/fa';
import { HiCheckCircle } from 'react-icons/hi';
import { useApp } from '../../../context/AppContext';
import { openAppStudio } from '../../../utils/appStudioUrl';
import { ReplacesLine, ReplacesTotal } from './HubReplaces';
import "./SalesPageNew.css";

// The card opens on Annually, the one public price ($25/month billed annually). The
// monthly figure, from the live Stripe price's currency options, shows only after a
// tap on Monthly.
const PRICING = {
   GBP: { symbol: "£", monthly: "29", annually: "25" },
   USD: { symbol: "$", monthly: "29", annually: "25" },
   EUR: { symbol: "€", monthly: "29", annually: "25" },
   SEK: { symbol: " kr", monthly: "400", annually: "333", suffix: true },
   AUD: { symbol: "AU$", monthly: "49", annually: "46" },
   CAD: { symbol: "CA$", monthly: "49", annually: "46" },
   NZD: { symbol: "NZ$", monthly: "60", annually: "54" },
};


// The six features that replace a tool creators pay for lead the list, in this
// order, each with a "Replaces" line (hubReplacesData.js). The other 20 follow in
// their old order.
const hubItemKeys = [
   { emoji: "🏪", key: "customStorefront", replaces: "storefront" },
   { emoji: "🤝", key: "unlimitedCoaching", replaces: "coaching" },
   { emoji: "🎓", key: "unlimitedCourses", replaces: "courses" },
   { emoji: "👥", key: "unlimitedCommunities", replaces: "communities" },
   { emoji: "💌", key: "unlimitedEmails", replaces: "emails" },
   { emoji: "📩", key: "autoDm", replaces: "autoDm" },
   { emoji: "♾️", key: "unlimitedProducts" },
   { emoji: "📱", key: "customerApps" },
   { emoji: "📦", key: "unlimitedDigitalProducts" },
   { emoji: "♻️", key: "unlimitedSubscriptions" },
   { emoji: "💬", key: "messaging" },
   { emoji: "📈", key: "insights" },
   { emoji: "💸", key: "instantPayouts" },
   { emoji: "😎", key: "customerAdmin" },
   { emoji: "🎟️", key: "promoCodes" },
   { emoji: "💳", key: "bnpl" },
   { emoji: "🌐", key: "customDomain" },
   { emoji: "✨", key: "customBranding" },
   { emoji: "💰", key: "affiliates" },
   { emoji: "🖥️", key: "landingPages" },
   { emoji: "🎯", key: "metaPixel" },
   { emoji: "🔗", key: "webhooks" },
   { emoji: "🧩", key: "apiAccess" },
   { emoji: "🤖", key: "mcp" },
   { emoji: "🙋", key: "realHumanSupport" },
   { emoji: "🏷️", key: "zeroCommission" },
];

const agentGroupDefs = [
   {
      bucketKey: "findClients",
      jobs: [
         { Icon: FaBullseye, key: "runMetaAds" },
         { Icon: FaColumns, key: "buildLandingPages" },
      ],
   },
   {
      bucketKey: "convertVisitors",
      jobs: [
         { Icon: FaMagic, key: "createContent" },
         { Icon: FaCommentDots, key: "findConvertVisitors" },
      ],
   },
];

export default function HubVsAgents({ windowWidth, logged_in, pre_influencer, influencer_bool, current_user, utmSource, utmMedium, utmCampaign, utmTerm, utmContent, referralId, passiveReferralId }) {
   const { t } = useTranslation();
   const { currency } = useApp();
   const [billing, setBilling] = useState('annually');

   const price = PRICING[currency] || PRICING.USD;
   const amount = billing === 'annually' ? price.annually : price.monthly;
   const isMobile = windowWidth < 768;
   const isSmall = windowWidth < 640;
   const isLarge = windowWidth >= 1024;

   const buildRegisterUrl = () => {
      let url = '/become-a-creator/register?source=new';
      const id = referralId || passiveReferralId;
      if (id) url += '&id=' + id;
      const utms = { utm_source: utmSource, utm_medium: utmMedium, utm_campaign: utmCampaign, utm_content: utmContent, utm_term: utmTerm };
      Object.entries(utms).forEach(([k, v]) => { if (v) url += '&' + k + '=' + encodeURIComponent(v); });
      return url;
   };

   const handleCTA = () => {
      if (logged_in === true) {
         if (pre_influencer === true && influencer_bool !== true) {
            window.location.assign(buildRegisterUrl());
         } else if (pre_influencer === false && influencer_bool !== true) {
            window.location.assign(buildRegisterUrl());
         } else {
            window.location.assign('/' + current_user);
         }
      } else {
         window.location.assign(buildRegisterUrl());
      }
   };

   return (
      <section id="how" style={{ maxWidth: 1152, margin: '0 auto', padding: '80px 24px' }}>
         <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 24,
            alignItems: 'start',
            alignContent: 'start',
         }}>
            {/* HUB CARD */}
            <div id="pricing" className="sp-card-lift" style={{
               borderRadius: 24, border: '1px solid var(--sp-bg-soft-2)', background: '#fff',
               padding: isMobile ? 24 : 32, scrollMarginTop: 96,
            }}>
               <h3 style={{ fontSize: isMobile ? 30 : 36, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--sp-ink)' }}>
                  {t('salesPageNew.hub.title')}
               </h3>
               <p style={{ marginTop: 12, color: 'var(--sp-brand, #3B55F3)', fontSize: 16, lineHeight: 1.7, textTransform: 'none', fontWeight: 600 }}>
                  {t('salesPageNew.hub.cardSubtitle')}
               </p>

               {/* Billing toggle */}
               <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                     display: 'inline-flex', alignItems: 'center', borderRadius: 999,
                     backgroundColor: '#F5F9FF', padding: 4, boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)',
                  }}>
                     <button
                        type="button"
                        aria-pressed={billing === 'monthly'}
                        onClick={() => setBilling('monthly')}
                        style={{
                           border: 'none', cursor: 'pointer', borderRadius: 999,
                           minHeight: 44, padding: '0 20px', fontSize: 15, fontWeight: 600,
                           transition: 'all 0.2s',
                           backgroundColor: billing === 'monthly' ? '#fff' : 'transparent',
                           color: billing === 'monthly' ? 'var(--sp-ink, #1a1a2e)' : 'var(--sp-text-soft, #6b7280)',
                           boxShadow: billing === 'monthly' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        }}
                     >
                        {t('salesPageNew.hub.monthly')}
                     </button>
                     <button
                        type="button"
                        aria-pressed={billing === 'annually'}
                        onClick={() => setBilling('annually')}
                        style={{
                           border: 'none', cursor: 'pointer', borderRadius: 999,
                           minHeight: 44, padding: '0 20px', fontSize: 15, fontWeight: 600,
                           transition: 'all 0.2s',
                           backgroundColor: billing === 'annually' ? '#fff' : 'transparent',
                           color: billing === 'annually' ? 'var(--sp-ink, #1a1a2e)' : 'var(--sp-text-soft, #6b7280)',
                           boxShadow: billing === 'annually' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        }}
                     >
                        {t('salesPageNew.hub.annually')}
                     </button>
                  </div>
               </div>

               {/* Price */}
               <div style={{ marginTop: 16, display: 'flex', alignItems: 'baseline', gap: 8, whiteSpace: 'nowrap' }}>
                  <span style={{
                     fontSize: isSmall ? 36 : (isMobile ? 48 : (isLarge ? 72 : 48)),
                     fontWeight: 800, letterSpacing: '-0.02em', lineHeight: isLarge ? 1 : 1.1,
                     color: 'var(--sp-ink)',
                  }}>
                     {price.suffix ? `${amount}${price.symbol}` : `${price.symbol}${amount}`}
                  </span>
                  <span style={{ fontSize: isSmall ? 12 : (isMobile ? 14 : (isLarge ? 18 : 13)), color: 'var(--sp-text-soft)' }}>
                     {billing === 'annually' ? t('salesPageNew.hub.billedAnnually') : t('salesPageNew.hub.perMonth', '/month')}
                  </span>
               </div>

               <ReplacesTotal billing={billing} currencies={PRICING} />

               {/* Features list */}
               <ul style={{
                  marginTop: 32, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '16px 24px', listStyle: 'none', padding: 0,
               }}>
                  {hubItemKeys.map(it => (
                     <li key={it.key} style={{ display: 'flex', alignItems: it.replaces ? 'flex-start' : 'center', gap: 12 }}>
                        <span style={{ fontSize: 18, lineHeight: '24px' }}>{it.emoji}</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sp-ink)', textTransform: 'none' }}>
                           {t(`salesPageNew.hub.features.${it.key}`)}
                           {it.replaces && <ReplacesLine row={it.replaces} billing={billing} currencies={PRICING} />}
                        </span>
                     </li>
                  ))}
               </ul>

               {/* What the annual plan adds on top of the features above. Annual only:
                   the figures are the difference between the two billing terms, so on
                   monthly there is nothing to total up. */}
               {billing === 'annually' && (
                  <div style={{
                     marginTop: 32, borderRadius: 16,
                     border: '1px solid var(--sp-border, #E4EBF6)',
                     background: 'var(--sp-bg-soft, #F8FAFC)',
                     padding: isMobile ? 20 : 24,
                  }}>
                     <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, paddingBottom: 12, borderBottom: '1px solid var(--sp-border, #E4EBF6)' }}>
                        <span style={{ fontSize: 14, color: 'var(--sp-text-soft)' }}>{t('salesPageNew.hub.value.twoMonths', '2 months of WithMe, free')}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#059669', whiteSpace: 'nowrap' }}>{t('salesPageNew.hub.value.included', 'included')}</span>
                     </div>
                     <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, padding: '12px 0', borderBottom: '1px solid var(--sp-border, #E4EBF6)' }}>
                        <span style={{ fontSize: 14, color: 'var(--sp-text-soft)' }}>{t('salesPageNew.hub.value.groupCoaching', 'Weekly Group Coaching Sessions')}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#059669', whiteSpace: 'nowrap' }}>{t('salesPageNew.hub.value.groupCoachingWorth', '$627')}</span>
                     </div>
                     <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, paddingTop: 16 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--sp-ink)' }}>{t('salesPageNew.hub.value.extra', 'Extra value with annual')}</span>
                        <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--sp-brand)', whiteSpace: 'nowrap' }}>{t('salesPageNew.hub.value.extraAmount', '$627 + 2 months')}</span>
                     </div>
                  </div>
               )}

               {/* CTA */}
               <button
                  onClick={handleCTA}
                  style={{
                     marginTop: 32, display: 'block', width: '100%', borderRadius: 12,
                     background: 'var(--sp-brand)', padding: '16px 24px', textAlign: 'center',
                     fontSize: 16, fontWeight: 600, color: '#fff', border: 'none', cursor: 'pointer',
                     transition: 'background 0.2s',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'var(--sp-brand-dark)'}
                  onMouseOut={e => e.currentTarget.style.background = 'var(--sp-brand)'}
               >
                  {t('salesPageNew.hub.startTrial')}
               </button>
               {billing === 'annually' && (
                  <p style={{ marginTop: 12, textAlign: 'center', fontSize: 14, fontWeight: 600, color: 'var(--sp-text-soft)' }}>
                     {t('salesPageNew.hub.value.ctaNote', 'Includes Weekly Group Coaching Sessions, worth $627')}
                  </p>
               )}

            </div>

            {/* RIGHT COLUMN */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24, position: isMobile ? 'relative' : 'sticky', top: isMobile ? 'auto' : 96 }}>
               {/* AGENTS CARD */}
               <div className="sp-card-lift" style={{
                  borderRadius: 24, border: '1px solid #FFD8B8', padding: 32,
                  background: 'linear-gradient(180deg, #FFF6EE, #FFFFFF)',
               }}>
                  <p className="sp-eyebrow">{t('salesPageNew.agents.eyebrow')}</p>
                  <h3 style={{ marginTop: 8, fontSize: isMobile ? 48 : 60, letterSpacing: '-0.02em', color: 'var(--sp-ink)' }}>
                     {t('salesPageNew.agents.title')}{" "}
                     <span className="sp-accent-italic-agent">{t('salesPageNew.agents.titleAccent')}</span>
                  </h3>
                  <p style={{ marginTop: 12, color: 'var(--sp-text-soft)', fontSize: 16, lineHeight: 1.7, textTransform: 'none', fontWeight: 400 }}>
                     {t('salesPageNew.agents.description')}
                  </p>

                  <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
                     {agentGroupDefs.map(group => (
                        <div key={group.bucketKey}>
                           <p className="sp-eyebrow">{t(`salesPageNew.agents.${group.bucketKey}`)}</p>
                           <ul style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12, listStyle: 'none', padding: 0 }}>
                              {group.jobs.map(it => (
                                 <li key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--sp-ink)' }}>
                                    <span style={{
                                       display: 'flex', alignItems: 'center', justifyContent: 'center',
                                       width: 36, height: 36, borderRadius: 12, background: '#fff',
                                       color: 'var(--sp-agent-ad)', boxShadow: 'inset 0 0 0 1px #FFD8B8',
                                    }}>
                                       <it.Icon size={18} />
                                    </span>
                                    <span style={{ fontSize: 14, fontWeight: 500, textTransform: 'none', lineHeight: 1.4 }}>{t(`salesPageNew.agents.${it.key}`)}</span>
                                 </li>
                              ))}
                           </ul>
                        </div>
                     ))}
                  </div>
               </div>

               {/* CUSTOM APP CARD */}
               <div className="sp-card-lift" style={{
                  borderRadius: 24, border: '1px solid var(--brand-color)', padding: '20px 24px',
                  // A solid brand fill would put the dark title on brand blue. The wash is a tint of the same token.
                  background: 'linear-gradient(180deg, color-mix(in srgb, var(--brand-color) 12%, white), #FFFFFF)',
               }}>
                  <p className="sp-eyebrow" style={{ color: 'var(--brand-color)', fontSize: 10 }}>{t('salesPageNew.customApp.eyebrow')}</p>
                  <h3 style={{ marginTop: 4, fontSize: isMobile ? 24 : 28, letterSpacing: '-0.02em', color: 'var(--sp-ink)' }}>
                     {t('salesPageNew.customApp.title')}{" "}
                     <span style={{ fontStyle: 'italic', color: 'var(--brand-color)' }}>{t('salesPageNew.customApp.titleAccent')}</span>
                  </h3>
                  <p style={{ marginTop: 8, color: 'var(--sp-text-soft)', fontSize: 14, lineHeight: 1.6, textTransform: 'none', fontWeight: 400 }}>
                     {t('salesPageNew.customApp.description')}
                  </p>

                  <button
                     onClick={() => openAppStudio(current_user, 'sales_page')}
                     style={{
                        marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        width: '100%', borderRadius: 12, background: 'var(--brand-color)', padding: '12px 20px',
                        textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#fff',
                        border: 'none', cursor: 'pointer', transition: 'background 0.2s',
                     }}
                     onMouseOver={e => e.currentTarget.style.background = 'var(--brand-color-hover)'}
                     onMouseOut={e => e.currentTarget.style.background = 'var(--brand-color)'}
                  >
                     <FaMobileAlt size={16} />
                     {t('salesPageNew.customApp.cta')}
                  </button>
               </div>
            </div>
         </div>
      </section>
   );
}
